'use client'

import { useId } from 'react'
import { panelFocusRing } from './SidePanel'

const controlClass = 'block w-full h-11 px-3 text-sm bg-background text-foreground border border-border rounded-none placeholder:text-muted-foreground transition-colors duration-150 focus:border-foreground focus:ring-0 focus:outline-none disabled:opacity-50'

function FieldLabel({ htmlFor, label, required }: { htmlFor: string, label: string, required?: boolean }) {
  return (
    <label htmlFor={htmlFor} className="block mb-2 text-[13px] text-foreground">
      {label}
      {required && <span aria-hidden className="text-muted-foreground"> *</span>}
    </label>
  )
}

export function TextField({ label, required, description, id, className = '', ...props }: React.InputHTMLAttributes<HTMLInputElement> & { label: string, required?: boolean, description?: string }) {
  const autoId = useId()
  const inputId = id ?? autoId
  return (
    <div className={className}>
      <FieldLabel htmlFor={inputId} label={label} required={required} />
      <input id={inputId} aria-required={required || undefined} className={controlClass} {...props} />
      {description && <p className="mt-1.5 text-xs text-muted-foreground">{description}</p>}
    </div>
  )
}

export function SelectField({ label, required, value, onChange, items, placeholder = 'Сонгоно уу', className = '' }: { label: string, required?: boolean, value: string, onChange: (value: string) => void, items: { value: string, label: string }[], placeholder?: string, className?: string }) {
  const id = useId()
  return (
    <div className={className}>
      <FieldLabel htmlFor={id} label={label} required={required} />
      <select id={id} aria-required={required || undefined} value={value} onChange={e => onChange(e.target.value)} className={`${controlClass} pr-9`}>
        {!value && <option value="" disabled>{placeholder}</option>}
        {items.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
      </select>
    </div>
  )
}

export function UnderlineTabs<T extends string>({ options, value, onChange, label }: { options: { value: T, label: string }[], value: T, onChange: (value: T) => void, label?: string }) {
  return (
    <div role="tablist" aria-label={label} className="flex items-center gap-6 border-b border-border overflow-x-auto scrollbar-hide">
      {options.map(option => (
        <button key={option.value} type="button" role="tab" aria-selected={option.value === value} onClick={() => onChange(option.value)} className={`shrink-0 -mb-px py-3 border-b text-[13px] whitespace-nowrap transition-colors duration-150 ${option.value === value ? 'border-foreground text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'} ${panelFocusRing}`}>
          {option.label}
        </button>
      ))}
    </div>
  )
}
